import React from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {FeedAuthor} from '../../../../services/socialFeedApi';
import {getProfilePath} from '../utils';
import styles from '../Home.module.css';

interface PostAuthorActionMenuProps {
  author: FeedAuthor | null | undefined;
  authorName: string;
  currentUserId?: string | null;
  isFollowPending?: boolean;
  onToggleFollow?: (authorId: string, isFollowing: boolean) => void;
}

const PostAuthorActionMenu: React.FC<PostAuthorActionMenuProps> = ({
  author,
  authorName,
  currentUserId,
  isFollowPending = false,
  onToggleFollow,
}) => {
  const navigate = useNavigate();
  const menuId = React.useId();
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const authorId = author?._id?.trim() || '';
  const profilePath = getProfilePath(authorId);
  const isOwnAuthor = Boolean(authorId) && authorId === currentUserId;
  const canOpenMenu = Boolean(authorId && profilePath && currentUserId && onToggleFollow && !isOwnAuthor);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  if (!profilePath) {
    return <>{authorName}</>;
  }

  if (!canOpenMenu) {
    return (
      <Link to={profilePath} className={styles.postAuthorLink}>
        {authorName}
      </Link>
    );
  }

  const followLabel = isFollowPending
    ? (author?.isFollowing ? 'Updating...' : 'Following...')
    : (author?.isFollowing ? 'Unfollow' : 'Follow');

  return (
    <div ref={menuRef} className={styles.postAuthorMenuContainer}>
      <button
        type="button"
        className={`${styles.postAuthorLink} ${styles.postAuthorMenuTrigger}`.trim()}
        aria-expanded={isMenuOpen}
        aria-haspopup="menu"
        aria-controls={isMenuOpen ? menuId : undefined}
        onClick={() => {
          setIsMenuOpen((currentIsMenuOpen) => !currentIsMenuOpen);
        }}
      >
        <span>{authorName}</span>
        <span className={styles.postAuthorMenuChevron} aria-hidden="true">▾</span>
      </button>

      {isMenuOpen ? (
        <div id={menuId} className={styles.authorActionMenu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.authorActionMenuItem}
            onClick={() => {
              setIsMenuOpen(false);
              navigate(profilePath);
            }}
          >
            Go to profile
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={isFollowPending}
            className={`${styles.authorActionMenuItem} ${isFollowPending ? styles.authorActionMenuItemDisabled : ''}`.trim()}
            onClick={() => {
              if (!authorId || !onToggleFollow) {
                return;
              }

              onToggleFollow(authorId, Boolean(author?.isFollowing));
            }}
          >
            {followLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(PostAuthorActionMenu);
